import {Component, OnInit, Output, EventEmitter, ViewChild} from '@angular/core';
import {AccountDetails} from '../../models/account';
import {AccountService} from '../../services/account.service';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TreeComponent} from 'angular-tree-component';
import * as _ from 'lodash';
import {Group} from '../../models/group';
import {GroupService} from '../../services/group.service';
import {SubscriptionplanService} from '../../services/subscriptionplan.service';
import {SubscriptionPlan} from '../../models/subscriptionplan';
import {Address} from '../../models/address';
import {ToastsManager} from 'ng2-toastr';
import {ContractService} from '../../services/contract.service';
import {Node} from '../../models/node';
import {Roles} from '../../models/roles';
import {CountryPickerService} from '../../utils/country-picker.service';
import {extractCountries} from '../../utils/country';
import {TranslateService} from '@ngx-translate/core';
@Component({
  selector: 'app-account-form',
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.css'],
})
export class AccountFormComponent implements OnInit {

  @ViewChild(TreeComponent)
  private tree: TreeComponent;

  affectableGroups: Group[] = null;
  affectablePlans: SubscriptionPlan[] = null;
  supportedLanguage: string[] = null;

  /** List of countries to display */
  countries: any[] = [];

  /** index of node id */
  index = 8;

  /** Value used to know if and where to display the role adding select box */
  showAddRole: number = null;

  /** Value used to know if and where to display the plan adding select box */
  showAddPlan: number = null;

  /**
   * Nodes to display roles
   * Note : the id corresponds to node id, and only make sens for the tree.
   */
  nodes = [];

  subscriptions = [];

  roles: string[] = [];

  userExists: boolean;

  /** Binded model */
  account: AccountDetails;

  passwordConfirmation: string;

  /**
   * Output to signal the creation of an account
   * It will allow us to add or update the line in the table of all accounts.
   */
  @Output() onValidation = new EventEmitter<void>();

  constructor(private accountService: AccountService,
              private groupService: GroupService,
              private planService: SubscriptionplanService,
              public activeModal: NgbActiveModal,
              public toastsManager: ToastsManager,
              public contractService: ContractService,
              public countryPicker: CountryPickerService,
              public translate: TranslateService) {

  }

  ngOnInit() {
    this.groupService.loadVisibleGroups().subscribe(groups => this.affectableGroups = groups);
    this.account.address = {} as Address;
    this.account.language = this.accountService.locale.toUpperCase();
    this.accountService.getSupportedLocales().then(l => this.supportedLanguage = l);
    this.countryPicker.getCountries().subscribe(res => this.countries = extractCountries(res, this.accountService.locale));

  }

  checkUserExists() {
    this.accountService.isUserExist(this.account.email)
      .subscribe(userExists => {
        this.userExists = userExists;
        if (this.userExists) {
          this.contractService.getSubscriptions(this.account.email).subscribe(subscriptions => {
            // build node object from subscriptions list
            this.createTreeFromSubscriptions(subscriptions);
          });
        }
      });
  }

  private createTreeFromSubscriptions(subscriptions) {
    this.subscriptions = subscriptions;
    const node = [];
    subscriptions.forEach(subscription => {
      const contractNode: Node = new Node();
      contractNode.name = subscription.groupName;
      contractNode.groupId = subscription.groupId;
      contractNode.children = [];
      subscription.contractTypes.forEach(contract => {
        const subNode: Node = new Node();
        subNode.name = contract.type.toString();
        subNode.contractId = contract.contractId;
        subNode.status = contract.status;
        subNode.children = [];
        contract.subscriptionPlans.forEach(subscriptionPlan => {
          const sub: Node = new Node();
          sub.name = subscriptionPlan.planName;
          sub.contractId = subscriptionPlan.contractId;
          sub.planId = subscriptionPlan.planId;
          sub.status = subscriptionPlan.status;
          subNode.children.push(sub);
        });
        contractNode.children.push(subNode);
      });
      node.push(contractNode);
    });
    this.nodes = node;
  }

  sendEmail(contractId: number) {
    this.contractService.sendEmail(contractId).subscribe(() => this.toastsManager.success(this.translate.instant("toast.confirmationMailSent")));
  }

  checkEmailFormat() {
    // official regex for email validation
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(this.account.email).toLowerCase());
  }

  checkPasswords() {
    const re = /^(?=\S*[a-z])(?=\S*[A-Z])(?=\S*\d)\S{8,}$/;
    return this.account.temporaryPassword
      && re.test(this.account.temporaryPassword)
      && this.account.temporaryPassword === this.passwordConfirmation;
  }

  /**
   * Create or update user profile calling services
   */
  submit(): void {
    this.accountService.create(this.account).subscribe(() => {
      this.onValidation.emit();
      this.checkUserExists();
    });
  }

  /**
   * Remove a node in the tree of roles
   * Call WS to remove role(s)
   * @param node
   * @param tree
   */
  removeNode(node, tree) {
    this.unsubscribeNodeContract(node);

    const parentNode = node.realParent ? node.realParent : node.treeModel.virtualRoot;
    _.remove(parentNode.data.children, function (child) {
      return child === node.data;
    });
    tree.treeModel.update();
    if (node.parent.data.children.length === 0) {
      node.parent.data.hasChildren = false;
    }
  }

  private unsubscribeNodeContract(node) {
    if (node.data && node.data.contractId) {
      this.contractService.unsubscribeFromContract(node.data.contractId).subscribe(() => {
        this.toastsManager.success(this.translate.instant("toast.unsubscribed"));
      });
    }
    if (node.children) {
      node.children.forEach(children => this.unsubscribeNodeContract(children));
    }
  }

  /**
   * On add button clicked, display the select box to add roles
   * @param node : the group node
   */
  displayAddRole(node): void {
    this.contractService.getApplicableTypeForGroup(node.data.groupId).subscribe(roles => {
      this.roles = [];
      roles.forEach(role => this.roles.push(role.toString()));
      this.showAddRole = node.id;
      this.showAddPlan = null; // close any select box that might already be opened
      node.expand();
    });
  }

  /**
   * On add button clicked, display the select box to add plans
   * @param node correspond to a node informing about a ROLE
   */
  displayAddPlan(node): void {
    // first load subscription plan available for the group
    let groupId;
    if ((groupId = node.parent.data.groupId) != undefined) {
      this.planService.loadSubscriptionPlansByGroup(groupId).subscribe(plans => this.affectablePlans = plans);
      this.showAddPlan = node.id;
      this.showAddRole = null; // close any select box that might already be open
      node.expand();
    }
  }

  /**    // TODO call WS to add contracts and subscription plan

   * Called when a subscription plan has been selected for the user within the specified group
   * @param value corresponds to the subscription plan ID
   * @param node node containing the role (group node)
   * @param tree
   */
  addPlan(value: string, node, tree): void {
    // first check if selected plan already exists in this group
    const existingPlan = this.tree.treeModel.getNodeBy(n => n.parent == node && n.data.planId == parseInt(value));

    if (!existingPlan) {

      if (!node.data.children) {
        node.data.children = [];
      }
      const selectedPlan = this.affectablePlans.find(p => p.id == parseInt(value));

      // TODO call WS to add contracts and subscription plan
      const groupId = node.parent.data.groupId;
      this.contractService.createContract(groupId, this.account.email, Roles.USER, selectedPlan.id).subscribe(contract => {
        const id = this.generateId();
        node.data.children.push({
          id: id,
          name: selectedPlan.name,
          planId: selectedPlan.id,
          contractId: contract.id,
          status: 'PENDING'
        });
        this.showAddPlan = null;
        this.tree.treeModel.update();
        this.tree.treeModel.getNodeById(node.id).expand();
      });
    }
  }

  /**
   * Update the tree with the specified role
   * Also, it calls the WS to update the contract if the role is maintainer or admin
   * @param value Adding a role under the specified group node
   * @param node group node
   * @param tree
   */
  addRole(value: string, node): void {
    // first check if role already exists in specific group
    const existingRole = this.tree.treeModel.getNodeBy(n => n.parent == node && n.data.name == value);
    if (existingRole) {
      // if role is of user type guide user to add a subscription plan
      if (value === Roles[Roles.USER]) {
        existingRole.expand();
        this.displayAddPlan(existingRole);
      }
    } else {
      if (!node.data.children) {
        node.data.children = [];
      }
      let groupId: number;
      if (node.data.groupId) {
        groupId = node.data.groupId;
      } else if (node.parent.data.groupId) {
        groupId = node.parent.data.groupId;
      } else {
        groupId = node.parent.data.parent.data.groupId;
      }
      const id = this.generateId();
      if (value !== Roles[Roles.USER]) {
        this.contractService.createContract(groupId,
          this.account.email,
          (value === Roles[Roles.ADMIN] ? Roles.ADMIN : Roles.MAINTAINER),
          null).subscribe(contract => {
          node.data.children.push({id: id, name: value, contractId: contract.id, status: 'PENDING'});
          this.showAddRole = null;
          this.tree.treeModel.update();
        });
      } else {
        node.data.children.push({id: id, name: value});
        this.showAddRole = null;
        this.tree.treeModel.update();
        this.tree.treeModel.getNodeById(node.id).expand();
        this.displayAddPlan(this.tree.treeModel.getNodeById(id));
      }
    }
  }

  /**
   * Trigered when a new group to affect user in has been selected
   * Push a new node with the specified group
   */
  addGroup(value: string) {
    // if account already has a contract with this group, expand the specific node

    const groupId = parseInt(value);
    const existingNode = this.tree.treeModel.getNodeBy(node => node.data.groupId == groupId);
    if (existingNode != undefined) {
      existingNode.expand();
    } else {  // new node to create
      const generatedId = this.generateId();
      this.nodes.push({
        id: generatedId,
        name: this.affectableGroups.find(g => g.id == groupId).name,
        children: [],
        groupId: groupId
      });
      this.tree.treeModel.update();
      const createdNode = this.tree.treeModel.getNodeById(generatedId);

      // guide user to next step by showing the select box for roles
      this.displayAddRole(createdNode);
    }
  }

  generateId(): number {
    return ++this.index;
  }
}



// WEBPACK FOOTER //
// ./src/app/accounts/account-form/account-form.component.ts